import clsx from 'clsx';
import { type ComponentPropsWithRef, type ForwardedRef, type ReactNode } from 'react';
import { Button, type ButtonProps, Heading, type HeadingProps } from 'react-aria-components';

export interface SidebarSectionProps extends ComponentPropsWithRef<'div'> {}

/**
 * Used to display a label and a list of items or tree.
 * @example
 * ```tsx
 * <SidebarSection>
 *   <SidebarSectionHeading>My Label</SidebarSectionHeading>
 *   <SidebarTree />
 * </SidebarSection>
 */
export function SidebarSection({ ref, ...props }: SidebarSectionProps) {
    return (
        <div
            ref={ref}
            {...props}
            className={clsx('group/sidebar-section flex flex-col gap-0.5', props.className)}
        />
    );
}

export interface SidebarSectionHeadingProps extends HeadingProps {}

export function SidebarSectionHeading({
    ref,
    ...props
}: SidebarSectionHeadingProps & { ref?: ForwardedRef<HTMLHeadingElement> }) {
    return (
        <Heading
            ref={ref}
            {...props}
            level={2}
            className={clsx(
                'flex items-center gap-1.5 truncate pl-1.5 text-ui-base leading-6 font-medium text-muted',
                props.className
            )}
        />
    );
}

export interface SidebarSectionHeadingButtonProps extends ButtonProps {
    children: ReactNode;
}

export function SidebarSectionHeadingButton({
    ref,
    ...props
}: SidebarSectionHeadingButtonProps & { ref?: ForwardedRef<HTMLButtonElement> }) {
    return (
        <Button
            ref={ref}
            {...props}
            className={clsx(
                '-ml-1.5 flex flex-1 items-center gap-1 rounded-sm pl-1.5 leading-6',
                'text-muted hover:bg-standout-side hover:text-neutral-high',
                'data-[focus-visible]:focus-ring',
                props.className
            )}
        />
    );
}

export interface SidebarSectionHeadingActionsProps extends ComponentPropsWithRef<'div'> {
    children: ReactNode;
}

export function SidebarSectionHeadingActions({ ref, ...props }: SidebarSectionHeadingActionsProps) {
    return (
        <div ref={ref} {...props} className={clsx('flex flex-1 justify-end', props.className)} />
    );
}
